import { useEffect, useState } from 'react';
import type { Track } from '../../api.js';
import { isActionable } from './statusMap.js';
import { useProgress } from './useProgress.js';

const TICK_MS = 15_000;
const SLOW_THRESHOLD_MS = 10 * 60 * 1000;
const BUILD_BAND_LOW = 30;
const BUILD_BAND_HIGH = 70;

const TERMINAL_STATES: Track['state'][] = ['DONE', 'FAILED_BUILD', 'FAILED_SCOPE', 'CANCELLED'];

function formatDuration(ms: number): string {
  const totalMinutes = Math.max(0, Math.round(ms / 60000));
  if (totalMinutes < 1) return '<1m';
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

export interface TimeInStateInfo {
  label: string;
  slow: boolean;
}

export function useTimeInState(track: Track, recentCompleted: Track[]): TimeInStateInfo {
  const [, tick] = useState(0);
  const progress = useProgress(track, recentCompleted);

  useEffect(() => {
    if (track.state !== 'BUILDING' && !isActionable(track.state)) return;
    const id = setInterval(() => tick((n) => n + 1), TICK_MS);
    return () => clearInterval(id);
  }, [track.state]);

  const referenceIso = track.state === 'BUILDING' ? track.approved_at ?? track.updated_at : track.updated_at;
  const elapsedMs = Math.max(0, Date.now() - new Date(referenceIso).getTime());

  if (track.state === 'BUILDING') {
    const frac = (progress - BUILD_BAND_LOW) / (BUILD_BAND_HIGH - BUILD_BAND_LOW);
    if (frac > 0 && frac < 1) {
      const totalMs = elapsedMs / frac;
      const remainingMs = Math.max(0, totalMs - elapsedMs);
      return { label: `${formatDuration(elapsedMs)} · est ~${formatDuration(remainingMs)} left`, slow: false };
    }
    return { label: `${formatDuration(elapsedMs)} building`, slow: false };
  }

  const isTerminal = TERMINAL_STATES.includes(track.state) || (track.state === 'MERGED' && track.deploy_status === 'SUCCESS');
  const slow = isActionable(track.state) && elapsedMs > SLOW_THRESHOLD_MS;
  const label = isTerminal ? formatDuration(elapsedMs) : `waiting ${formatDuration(elapsedMs)}`;

  return { label, slow };
}
